import React, { useState } from 'react'
import styles from './style/configuration_card_style.module.scss'
import CopyIcon from './assets/copy-icon.svg'
import DownloadIcon from './assets/download-icon.svg'
import TrashIcon from './assets/trash-icon.svg'
import { capitalizeFirstLetter } from '../../../../helpers'
import { handlePlanPdfDownload } from '../../utils'
import LocalizedLink from '../../../global/LocalizedLink'
import Modal from '../../../global/Modal'
import confirm from '../../../global/extendedConfirm'
import Button from '../../../global/Button'
import Translation from '../../../global/Translation'
import moment from 'moment/moment'

const IN_DECIMALS = 2

const joinOrDash = (parts) => parts.filter(Boolean).join(' / ') || '—'

const trans = (intl, id, defaultMessage) => intl.formatMessage({ id, defaultMessage })

// ─── Spec builders ───────────────────────────────────────────────────────────

const SPEC_BUILDERS = {
    washbasins: (p, { measureFromMetric, units, intl }) => {
        const material = p.material || {}
        const countertop = p.countertop || {}
        const skirt = countertop.skirt || {}
        //const legs = p.legs || {}
        const basins = p.basins || {}
        const tapDrills = p.tapDrills || {}
        const towelRails = p.towelRails || {}
        const soapHolders = p.soapHolders || {}
        const productName = p.product || ''
        const translatedProductName = productName ? trans(intl, productName, productName) : ''
        const modelName = p.model || ''

        const colorName = material.colorName || ''
        const finishName = material.finish
            ? intl.formatMessage({ id: material.finish })
            : (material.finishName || '')

        const color = joinOrDash([capitalizeFirstLetter(colorName), capitalizeFirstLetter(finishName)])

        let encimera = '—'
        if (countertop.length && countertop.width) {
            encimera =
                measureFromMetric(countertop.length, IN_DECIMALS) +
                ' x ' +
                measureFromMetric(countertop.width, IN_DECIMALS) +
                ' ' + units
        } else if (countertop.dimension) {
            encimera = countertop.dimension
        }

        let canto = '—'
        if (skirt.hasSkirt) {
            const skirtLength = skirt.length
                ? measureFromMetric(skirt.length, IN_DECIMALS) + ' ' + units
                : ''
            const translatedSides = Array.isArray(skirt.sides)
                ? skirt.sides
                    .map((side) => {
                        const sideValue = String(side || '').toLowerCase()
                        const defaultMessage = sideValue === 'left'
                            ? 'left'
                            : sideValue === 'right'
                                ? 'right'
                                : String(side || '')

                        return trans(intl, sideValue, defaultMessage)
                    })
                    .join(', ')
                : ''

            const sides = skirt.sides ? trans(intl, 'sides', 'Sides') + ': ' + translatedSides.toLowerCase() : ''
            canto = joinOrDash([skirtLength, sides])
        }

        const cubetas = basins && basins.items && basins.items.length ? basins.items.length : null

        const extrasParts = []
        if (tapDrills && tapDrills.items && tapDrills.items.length) {
            extrasParts.push(trans(intl, 'tap_drills', 'Tap drills') + ': ' + tapDrills.items.length)
        }
        if (towelRails && towelRails.items && towelRails.items.length) {
            const sizes = towelRails.items
                .filter((i) => i.length)
                .map((i) => measureFromMetric(i.length, IN_DECIMALS) + ' ' + units)
                .join(', ')
            let str = trans(intl, 'towel_rails', 'Towel rails') + ': ' + towelRails.items.length
            if (sizes) str += ' (' + sizes + ')'
            extrasParts.push(str)
        }
        if (soapHolders && soapHolders.items && soapHolders.items.length) {
            extrasParts.push(trans(intl, 'soap_holders', 'Soap holders') + ': ' + soapHolders.items.length)
        }

        const product = joinOrDash([translatedProductName, capitalizeFirstLetter(modelName)])

        return [
            {
                labelId: 'product', labelDefault: 'Product',
                label: trans(intl, 'product', 'Product'),
                value: product
            },
            {
                labelId: 'color', labelDefault: 'Color',
                label: trans(intl, 'color', 'Color'),
                value: color
            },
            {
                labelId: 'countertop', labelDefault: 'Countertop',
                label: trans(intl, 'countertop', 'Countertop'),
                value: encimera
            },
            {
                labelId: 'edge', labelDefault: 'Edge',
                label: trans(intl, 'edge', 'Edge'),
                value: canto
            },
            {
                labelId: 'basins', labelDefault: 'Basins',
                label: trans(intl, 'basins', 'Basins'),
                value: cubetas !== null ? String(cubetas) : null,
                hidden: cubetas === null
            },
            {
                labelId: 'extras', labelDefault: 'Extras',
                label: trans(intl, 'extras', 'Extras'),
                value: extrasParts.join(' / ') || null,
                hidden: extrasParts.length === 0,
                extraClass: styles['config-card__spec--extras']
            }
        ]
    },

    showertray: (p, { measureFromMetric, units, intl }) => {
        const material = p.material || {}
        const tray = p.tray || p.countertop || {}
        const drain = p.drain || {}
        const tapDrills = p.tapDrills || {}
        const towelRails = p.towelRails || {}
        const soapHolders = p.soapHolders || {}
        const productName = p.product || ''
        const translatedProductName = productName ? trans(intl, productName, productName) : ''
        const modelName = p.model || ''

        const colorName = material.colorName || ''
        const finishName = material.finish
            ? intl.formatMessage({ id: material.finish })
            : (material.finishName || '')
            
        const color = joinOrDash([capitalizeFirstLetter(colorName), capitalizeFirstLetter(finishName)])

        let dimensions = '—'
        if (tray.length && tray.width) {
            dimensions =
                measureFromMetric(tray.length, IN_DECIMALS) +
                ' x ' +
                measureFromMetric(tray.width, IN_DECIMALS) +
                ' ' + units
        } else if (tray.dimension) {
            dimensions = tray.dimension
        }

        const drainPosition = drain.position || drain.type || null

        const extrasParts = []
        if (tapDrills && tapDrills.items && tapDrills.items.length) {
            extrasParts.push(trans(intl, 'tap_drills', 'Tap drills') + ': ' + tapDrills.items.length)
        }
        if (towelRails && towelRails.items && towelRails.items.length) {
            const sizes = towelRails.items
                .filter((i) => i.length)
                .map((i) => measureFromMetric(i.length, IN_DECIMALS) + ' ' + units)
                .join(', ')
            let str = trans(intl, 'towel_rails', 'Towel rails') + ': ' + towelRails.items.length
            if (sizes) str += ' (' + sizes + ')'
            extrasParts.push(str)
        }
        if (soapHolders && soapHolders.items && soapHolders.items.length) {
            extrasParts.push(trans(intl, 'soap_holders', 'Soap holders') + ': ' + soapHolders.items.length)
        }

        const product = joinOrDash([translatedProductName, capitalizeFirstLetter(modelName)])

        return [
             {
                labelId: 'product', labelDefault: 'Product',
                label: trans(intl, 'product', 'Product'),
                value: product
            },
            {
                labelId: 'color', labelDefault: 'Color',
                label: trans(intl, 'color', 'Color'),
                value: color
            },
            {
                labelId: 'dimensions', labelDefault: 'Dimensions',
                label: trans(intl, 'dimensions', 'Dimensions'),
                value: dimensions
            },
            {
                labelId: 'drain_position', labelDefault: 'Drain position',
                label: trans(intl, 'drain_position', 'Drain position'),
                value: drainPosition,
                hidden: !drainPosition
            },
            {
                labelId: 'extra', labelDefault: 'Extra',
                label: trans(intl, 'extra', 'Extra'),
                value: extrasParts.join(' / ') || null,
                hidden: extrasParts.length === 0,
                extraClass: styles['config-card__spec--extras']
            }
        ]
    }

    // flooring: (p, ctx) => [ ... ],
}

const buildGenericSpecs = (p, { intl }) => {
    const material = p.material || {}
    const colorName = material.colorName || ''
    const finishName = material.finish
        ? intl.formatMessage({ id: material.finish })
        : (material.finishName || '')
    return [
        {
            labelId: 'color', labelDefault: 'Color',
            label: trans(intl, 'color', 'Color'),
            value: joinOrDash([colorName, finishName])
        }
    ]
}

const getSpecs = (payload, productType, ctx) => {
    const builder = SPEC_BUILDERS[productType] || buildGenericSpecs
    return builder(payload, ctx).filter((spec) => !spec.hidden)
}

const ConfigurationCard = (props) => {
    const {
        item,
        removeFavoriteConfiguration,
        fetchFavoriteConfigurations,
        cloneConfiguration,
        pageFavoriteConfig,
        sizeFavoriteConfig,
        measureFromMetric,
        units,
        intl,
        triggerCopyConfiguration,
        triggerDeleteConfiguration
    } = props

    const [showCloneModal, setShowCloneModal] = useState(false)
    const [newConfigName, setNewConfigName] = useState('')
    const [isCloning, setIsCloning] = useState(false)

    const handleOpenCloneModal = () => {
        setNewConfigName('')
        setShowCloneModal(true)
    }
    const handleCloseCloneModal = () => {
        setShowCloneModal(false)
        setNewConfigName('')
    }

    const handleCloneConfirm = () => {
        if (!newConfigName.trim()) return
        setIsCloning(true)
        cloneConfiguration(item.ref, newConfigName.trim())
            .then(() => {
                fetchFavoriteConfigurations(pageFavoriteConfig, sizeFavoriteConfig)
                handleCloseCloneModal()
            })
            .finally(() => setIsCloning(false))
    }

    const handlePlanDownload = () => {
        if (!item.drawingUrl) return
        const url =
            process.env.REACT_APP_API_HOST +
            '/helper/view-pdf-url/?url=' +
            encodeURIComponent(item.drawingUrl)
        handlePlanPdfDownload(url, (item.payload || {}).model, item.ref)
    }

    const handleRemoveFavoriteConfig = () =>
        confirm(
            trans(intl, 'are_you_sure_you_want_to_delete_the_configuration', 'Are you sure you want to delete the configuration?'),
            {
                isV2: true,
                title: trans(intl, 'delete_this_configuration', 'Delete this configuration'),
                textCancel: trans(intl, 'cancel', 'Cancel'),
                textOk: trans(intl, 'delete', 'Delete'),
                advise: trans(intl, 'this_action_is_permanent_and_you_will_not_be_able_to_recover_your_saved_settings', 'This action is permanent and you will not be able to recover your saved settings.')
            }
        ).then(
            () => {
                triggerDeleteConfiguration()
                return removeFavoriteConfiguration(item.ref).then(() =>
                    fetchFavoriteConfigurations(pageFavoriteConfig, sizeFavoriteConfig)
                )
            },
            () => Promise.reject()
        )

    const p = item.payload || {}
    const ctx = { measureFromMetric, units, intl }
    const specs = getSpecs(p, p.product, ctx)

    return (
        <article className={styles['config-card']}>
            <div className={styles['config-card__image-wrapper']}>
                <img
                    src={item.defaultImageModelUrl}
                    alt={capitalizeFirstLetter(item.model)}
                    className={styles['config-card__image']}
                />
            </div>

            <div className={styles['config-card__body']}>
                <h2 className={styles['config-card__title']}>
                    {item.configName || 'Config #' + item.id}
                </h2>

                <div className={styles['config-card__actions']}>
                    <div>
                        <span className={styles['config-card__meta']}>
                            {moment(item.createdAt).format('L')}
                        </span>
                    </div>
                    <div className={styles['config-card__action-buttons']}>
                        {item.drawingUrl && (
                            <button
                                className={styles['config-card__action-btn']}
                                onClick={handlePlanDownload}
                                gtm-label="saved-custom-drafts_button_download-custom-draft"
                            >
                                <img className="download-icon" alt="Download" src={DownloadIcon} />
                            </button>
                        )}
                        <button
                            className={styles['config-card__action-btn']}
                            onClick={() => {
                                handleOpenCloneModal()
                            }}
                            gtm-label="saved-custom-drafts_button_copy-custom-draft"
                        >
                            <img className="copy-icon" alt="Copy icon" src={CopyIcon} />
                        </button>
                        <button
                            className={styles['config-card__action-btn']}
                            onClick={() => {
                                handleRemoveFavoriteConfig()
                            }}
                            gtm-label="saved-custom-drafts_button_delete-custom-draft"
                        >
                            <img className="trash-icon" alt="Trash icon" src={TrashIcon} />
                        </button>
                    </div>
                </div>

                <ul className={styles['config-card__specs']}>
                    {specs.map((spec) => (
                        <li
                            key={spec.labelId}
                            className={[styles['config-card__spec'], spec.extraClass]
                                .filter(Boolean)
                                .join(' ')}
                        >
                            <span>{spec.label}:</span>{' '}
                            {spec.value}
                        </li>
                    ))}
                </ul>
            </div>

            <div className={styles['config-card__footer']}>
                <LocalizedLink
                    routeId="ROUTE_CONFIG_CUT_TO_SIZE"
                    params={{
                        type: p.product,
                        model: p.model.toLowerCase().replace(' ', '_'),
                        fabricator: !!item.thirdParty ? 'local' : 'global',
                        refcode: item.ref
                    }}
                >
                    <button className={styles['config-card__cta']} gtm-label="saved-custom-drafts_link_re-edit">
                        <Translation id="continue_configuring" defaultMessage="Continue configuring" />{' '}
                        <i className="fas fa-chevron-right" />
                    </button>
                </LocalizedLink>
            </div>

            {showCloneModal && (
                <Modal
                    title={trans(intl, 'duplicate_this_configuration', 'Duplicate this configuration')}
                    onClose={handleCloseCloneModal}
                    size="small"
                    customClass={styles['clone-modal']}
                    footer={
                        <React.Fragment>
                            <button
                                className={styles['clone-modal__cancel-btn']}
                                onClick={handleCloseCloneModal}
                                disabled={isCloning}
                            >
                                <Translation id={'cancel'} defaultMessage={'Cancel'} />
                            </button>
                            <Button
                                inverted
                                onClick={() => {
                                    handleCloneConfirm()
                                    triggerCopyConfiguration()
                                }}
                                disabled={!newConfigName.trim() || isCloning}
                            >
                                <Translation id={'save'} defaultMessage={'Save'} />
                            </Button>
                        </React.Fragment>
                    }
                    customClassFooter={styles['clone-modal__footer']}
                >
                    <p className={styles['clone-modal__description']}>
                        <Translation id={'enter_a_name_for_the_copy_of_this_configuration'}
                                     defaultMessage={'Enter a name for the copy of this configuration'} />:
                    </p>
                    <input
                        type="text"
                        className="form-control"
                        placeholder={trans(intl, 'name', 'Name')}
                        value={newConfigName}
                        onChange={(e) => setNewConfigName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleCloneConfirm()}
                        autoFocus
                    />
                </Modal>
            )}
        </article>
    )
}

export default ConfigurationCard